import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DateService {
  private currentTimeSubject = new BehaviorSubject<Date>(new Date());
  public currentTime$ = this.currentTimeSubject.asObservable();

  constructor() {
    // Update current time every second
    setInterval(() => {
      this.currentTimeSubject.next(new Date());
    }, 1000);
  }

  /**
   * Get a date X days ago and return both dates formatted
   * @param days Number of days to subtract (0 for today)
   * @returns Object with CalDate (past date) and TodyDate (today) in YYYY-MM-DD format
   */
  public subtractDate(days: number): { CalDate: string; TodyDate: string } {
    const now = new Date();
    const pastDate = new Date(now);
    pastDate.setDate(now.getDate() - days);
    return { CalDate: this.formatDate(pastDate), TodyDate: this.formatDate(now) };
  }

  /**
   * Format a date as YYYY-MM-DD (UTC)
   * @param date Date to format
   * @returns Formatted date string
   */
  public formatDate(date: Date): string {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Format time as HH:mm:ss (UTC)
   * @param date Date to format
   * @returns Formatted time string
   */
  public formatTime(date: Date): string {
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  /**
   * Format date and time as YYYY-MM-DD HH:mm:ss (UTC)
   * @param date Date to format
   * @returns Formatted date-time string
   */
  public formatDateTime(date: Date): string {
    return `${this.formatDate(date)} ${this.formatTime(date)}`;
  }

  /**
   * Get the start of a day (00:00:00) as a formatted string
   * @param date Date to get start of day for
   * @returns Formatted start of day string
   */
  public startOfDay(date: Date): string {
    return `${this.formatDate(date)} 00:00:00`;
  }

  /**
   * Get the end of a day (23:59:59) as a formatted string
   * @param date Date to get end of day for
   * @returns Formatted end of day string
   */
  public endOfDay(date: Date): string {
    return `${this.formatDate(date)} 23:59:59`;
  }

  /**
   * Get current date-time formatted
   * @returns Current date-time formatted as YYYY-MM-DD HH:mm:ss
   */
  public now(): string {
    return this.formatDateTime(new Date());
  }
}
